/**
 * Pure permission-resolution logic for Teams & Permissions (KAN-14).
 *
 * Extracted from use-permissions.ts with the precedence order intact; the one
 * behavioural change is readLevel's PERMISSION_LEVELS check, so a step storing
 * an unknown level no longer wins the chain (see below). Kept free of React and
 * store imports so it stays independently verifiable; use-permissions imports
 * resolvePermission and builds every resolver on top of it.
 */
import { PERMISSION_LEVELS } from '@/types/settings';
import type { PermissionCategoryKey, Team } from '@/types/settings';

/** Read one level off a team. `itemId === null` reads the category-wide default.
 *  Returns null when the team has no opinion, or stores a value outside
 *  PERMISSION_LEVELS (legacy `'inherit_global'`, empty strings, stray objects) —
 *  those fall through rather than pinning an item to an unknown level. */
function readLevel<T extends string>(
  team: Team | null,
  category: PermissionCategoryKey,
  itemId: string | null
): T | null {
  const cat = team?.permissions?.[category];
  if (!cat) return null;
  const raw = itemId === null ? cat.default : cat[itemId];
  if (typeof raw !== 'string') return null;
  return (PERMISSION_LEVELS[category] as readonly string[]).includes(raw) ? (raw as T) : null;
}

/**
 * Resolve the effective permission for a specific item within a category.
 *
 * The user's own group is consulted fully before the _default group:
 *   1. user's group   — this exact item
 *   2. user's group   — category-wide default
 *   3. _default group — this exact item
 *   4. _default group — category-wide default
 *   5. hardcoded fallback
 *
 * The KAN-14 bug was a category-wide default written into a named group
 * *implicitly* (the editor used to seed one on the first per-item edit), which
 * made step 2 always hit and the _default group unreachable. The editor no
 * longer seeds, so step 2 only fires on a level an admin actually picked.
 */
export function resolvePermission<T extends string>(
  userTeam: Team | null,
  defaultTeam: Team | null,
  category: PermissionCategoryKey,
  itemId: string,
  fallback: T
): T {
  return (
    readLevel<T>(userTeam, category, itemId)
    ?? readLevel<T>(userTeam, category, null)
    ?? readLevel<T>(defaultTeam, category, itemId)
    ?? readLevel<T>(defaultTeam, category, null)
    ?? fallback
  );
}
